import { useEffect, useRef, useState } from 'react';
import { useCall } from '@/contexts/call-context';
import { FolderOpen, Phone, PhoneOff, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import ringtoneUrl from '@assets/videocall_1777296943147.mp3';
import { getUserColor } from '@/lib/user-color';
import { IncomingCallDialog } from '@/components/calls/IncomingCallDialog';

interface AudioSettings {
  audioNotificationsEnabled: boolean;
}

export function IncomingCallModal() {
  const { incomingCall: pendingIncomingCall, activeCall, acceptIncoming, declineIncoming } = useCall();
  const incomingCall = activeCall ? null : pendingIncomingCall;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const acceptRef = useRef<HTMLButtonElement | null>(null);

  const { data: audioSettings } = useQuery<AudioSettings>({
    queryKey: ['/api/user/audio-settings'],
    enabled: !!incomingCall,
  });
  // Server default is enabled; only an explicit opt-out silences the ring.
  // (Defaulting to false kept the ringtone off whenever the settings fetch
  // was slow or failed.)
  const soundEnabled = audioSettings?.audioNotificationsEnabled ?? true;
  // The browser refused play() (autoplay policy: no user gesture on this
  // page yet — common right after login or in a freshly opened tab). Say so
  // and retry on the next gesture instead of ringing silently.
  const [ringtoneBlocked, setRingtoneBlocked] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (!(incomingCall && soundEnabled)) {
      a.pause();
      a.currentTime = 0;
      setRingtoneBlocked(false);
      return;
    }
    let disposed = false;
    const detachGesture = () => {
      document.removeEventListener('pointerdown', onGesture, true);
      document.removeEventListener('keydown', onGesture, true);
    };
    const tryPlay = () => {
      a.loop = true;
      a.volume = 0.5;
      a.play().then(
        () => { if (!disposed) { setRingtoneBlocked(false); detachGesture(); } },
        () => {
          if (disposed) return;
          setRingtoneBlocked(true);
          document.addEventListener('pointerdown', onGesture, true);
          document.addEventListener('keydown', onGesture, true);
        },
      );
    };
    function onGesture() {
      detachGesture();
      if (!disposed) tryPlay();
    }
    tryPlay();
    return () => {
      disposed = true;
      detachGesture();
      a.pause();
      a.currentTime = 0;
    };
  }, [incomingCall, soundEnabled]);

  const project = incomingCall?.session.project;
  const [projectImageFailed, setProjectImageFailed] = useState(false);

  useEffect(() => {
    setProjectImageFailed(false);
  }, [project?.id, project?.thumbnail]);

  if (!incomingCall) return null;

  const session = incomingCall.session;
  // Server bakes initiatorName + initiatorColorCode into every call summary
  // (see CallSummary in server/routes/calls.ts) so the modal renders the real
  // caller's name and avatar the instant the ring arrives — no async lookup
  // required. Same `formatUserDisplayName` helper that the LiveKit token mint
  // uses, so the ring screen and the in-call tile always agree.
  const initiatorName = session.initiatorName;
  const avatarLetter = (initiatorName.trim()[0] || '?').toUpperCase();
  const avatarColor = getUserColor(session.initiatorColorCode);
  const otherCount = session.participants.length - 1;

  return (
    <>
      {/* Keep audio in this component's commit tree rather than the portal so
          its ref is ready when the ringtone effect runs. */}
      <audio ref={audioRef} src={ringtoneUrl} preload="auto" />
      <IncomingCallDialog
        title={`Incoming ${session.type} call from ${initiatorName}`}
        description="Choose Accept to answer the call or Decline to reject it."
        initialFocusRef={acceptRef}
      >
      <div className="flex flex-col items-center gap-4">
        <div
          className="h-20 w-20 rounded-full flex items-center justify-center text-2xl font-bold text-white animate-pulse"
          style={{ backgroundColor: avatarColor }}
        >
          {avatarLetter}
        </div>
        <div className="text-center">
          <div className="text-base font-semibold">{initiatorName}</div>
          <div className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1">
            {session.type === 'video' ? <Video className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
            Incoming {session.type} call
            {otherCount > 1 && <span> · group ({session.participants.length})</span>}
          </div>
          {ringtoneBlocked && (
            <div
              data-testid="incoming-call-ringtone-blocked"
              className="mt-2 text-[11px] text-amber-500"
            >
              Ringtone muted by your browser until you click or press a key.
            </div>
          )}
        </div>
        {project && (
          <div
            data-testid="incoming-call-project"
            className="w-full min-w-0 rounded-xl border border-border bg-muted/40 p-3 flex items-center gap-3 text-left"
          >
            <div className="h-16 w-24 shrink-0 rounded-lg overflow-hidden border border-border bg-muted flex items-center justify-center">
              {project.thumbnail && !projectImageFailed ? (
                <img
                  data-testid="incoming-call-project-thumbnail"
                  src={project.thumbnail}
                  alt={`${project.name || project.id} project thumbnail`}
                  className="h-full w-full object-cover"
                  onError={() => setProjectImageFailed(true)}
                />
              ) : (
                <FolderOpen
                  data-testid="incoming-call-project-thumbnail-fallback"
                  aria-label="Project thumbnail unavailable"
                  className="h-7 w-7 text-muted-foreground"
                />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Project
              </div>
              <div
                data-testid="incoming-call-project-name"
                className="font-semibold leading-snug break-words whitespace-normal"
              >
                {project.name || 'Unnamed project'}
              </div>
              <div
                data-testid="incoming-call-project-id"
                className="mt-0.5 font-mono text-xs text-muted-foreground break-all"
              >
                ID: {project.id}
              </div>
            </div>
          </div>
        )}
        <div className="flex gap-3 w-full mt-2">
          <Button
            variant="destructive"
            className="flex-1 gap-2"
            onClick={() => { void declineIncoming(); }}
          >
            <PhoneOff className="h-4 w-4" /> Decline
          </Button>
          <Button
            ref={acceptRef}
            className="flex-1 gap-2 bg-green-600 hover:bg-green-700 text-white"
            onClick={() => { void acceptIncoming(); }}
          >
            <Phone className="h-4 w-4" /> Accept
          </Button>
        </div>
      </div>
      </IncomingCallDialog>
    </>
  );
}
